import { useContext } from "react";

import { useSelector } from "react-redux";

import {
  FAST_CHANNELS_ROUTE,
  INSIGHTS_ROUTE,
  LOGIN_ROUTE,
  PROJECTS_ROUTE,
} from "../components/routes/routes";
import { AbilitiesContext } from "../components/utils/abilities/AbilitiesProvider";
import { ORGANIZATION_TYPE } from "../constants/organization";
import {
  ANALYST,
  ANALYST_ADMIN,
  BUSINESS_MANAGER,
  CONTENT_MANAGER,
  CONTENT_MANAGER_ADMIN,
  EDITOR_ADMIN,
  EDITORIAL_MANAGER,
  FAST_ADMIN,
  FAST_ANALYST,
  MODERATOR,
  MODERATOR_ADMIN,
  OWNER,
  PROJECT_ADMIN,
} from "../constants/roles";

import type { RoutePath } from "../components/routes/routes";
import type { Role } from "../constants/roles";
import type { AppState } from "../reducers";
import type {
  Abilities,
  AbilitiesKeys,
  AbilitiesNestedKeys,
  AbilitiesWithNestedRights,
  Ability,
} from "../types";
import type { OrganizationOptions } from "../types/organization.types";

interface IAbilitiesContext {
  abilities: Abilities;
  orgaAbility: Ability;
  roles: Role[];
}

type CanWrite = (
  key: AbilitiesKeys,
  nested?: AbilitiesNestedKeys | null,
  abilities?: Abilities,
) => boolean;

type CanWriteOrRead = (
  key: AbilitiesKeys,
  param: Ability,
  abilities: Abilities,
  nested?: AbilitiesNestedKeys,
) => boolean;

type GetAbility = (
  key: AbilitiesKeys | null,
  nested?: AbilitiesNestedKeys | null,
  abilities?: Abilities,
) => Ability;

type HasOrgaAbility = () => boolean;

type HasRole = (roles: Role[]) => boolean;

export interface CanAccessReturn {
  adminUsers: boolean;
  bulks: boolean;
  insights: boolean;
  castings: boolean;
  lives: boolean;
  contents: boolean;
  contentsWrite: boolean;
  contentsAnalytics: boolean;
  fastChannels: boolean;
  fastChannelsWrite: boolean;
  projects: boolean;
  ottContents: boolean;
  storageAccess: boolean;
  storageAccessWrite: boolean;
  embedProfiles: boolean;
  embedProfilesWrite: boolean;
  project: {
    allUsers: boolean;
    freeCopies: boolean;
    gifts: boolean;
    layout: boolean;
    configuration: boolean;
    offers: boolean;
    advertisingsSponsorings: boolean;
    displays: boolean;
    notifications: boolean;
    smartlists: boolean;
    collections: boolean;
    series: boolean;
    smartlistsAudios: boolean;
    bulk: boolean;
    contentsAnalytics: boolean;
    transactionsAnalytics: boolean;
    offersAnalytics: boolean;
    usersAnalytics: boolean;
    community: boolean;
    offersView: boolean;
    usersWrite: boolean;
    freeCopiesWrite: boolean;
    giftsWrite: boolean;
    offersWrite: boolean;
    projectWrite: boolean;
    advertisingsSponsoringsWrite: boolean;
    displaysWrite: boolean;
    advertisingsSponsoringsAnalytics: boolean;
    displaysAnalytics: boolean;
    platform: boolean;
    offerManagement: boolean;
    contentManagement: boolean;
    analyticManagement: boolean;
    dashboard: boolean;
    salesWrite: boolean;
    organization: boolean;
    bulkWrite: boolean;
    fastWrite: boolean;
  };
}

type GetHomePageURL = () => RoutePath;

interface ReturnTypeUseAbility {
  canWrite: CanWrite;
  canWriteOrRead: CanWriteOrRead;
  getAbility: GetAbility;
  hasOrgaAbility: HasOrgaAbility;
  hasRole: HasRole;
  canAccess: (orgaOptions: OrganizationOptions) => CanAccessReturn;
  getHomePageURL: GetHomePageURL;
  isFastOnly: boolean;
  isOttOnly: boolean;
}

const useAbility = (): ReturnTypeUseAbility => {
  const context = useContext<IAbilitiesContext>(AbilitiesContext);

  const organizationType = useSelector(
    (state: AppState) => state.fetchReducer.orga_config.data?.organization_type,
  );

  // FAST-only and OTT-only plans hide a subset of the catalogue. These gates
  // are combined (AND) with the existing role / orgaOptions checks.
  const isFastOnly = organizationType === ORGANIZATION_TYPE.FAST;
  const isOttOnly = organizationType === ORGANIZATION_TYPE.OTT;

  const getAbility: GetAbility = (
    key,
    nestedKey = null,
    abilities = context?.abilities || null,
  ) => {
    if (!key || !abilities || !(key in abilities)) return null;

    if (
      nestedKey &&
      abilities[key] !== null &&
      typeof abilities[key] !== "string"
    ) {
      return abilities[key as keyof AbilitiesWithNestedRights][
        nestedKey as keyof (typeof abilities)[typeof key]
      ];
    }

    return abilities[
      key as keyof Omit<Abilities, keyof AbilitiesWithNestedRights>
    ];
  };

  const hasOrgaAbility: HasOrgaAbility = () => context?.orgaAbility === "write";

  const canWriteOrRead: CanWriteOrRead = (key, param, abilities, nested) => {
    const ability = getAbility(key, nested, abilities);

    if (!key || !ability) return false;

    return ability === param;
  };

  const canWrite: CanWrite = (key, nested, abilities) => {
    const ability = getAbility(key, nested, abilities);

    if (!key || !ability) return false;

    return ability === "write";
  };

  const hasRole: HasRole = (roles) => {
    if (!context?.roles?.length || !roles?.length) return false;

    return roles.some((role) => context.roles.includes(role));
  };

  const homePageUrlByRole: Record<Role, RoutePath> = {
    [OWNER]: INSIGHTS_ROUTE,
    [EDITOR_ADMIN]: INSIGHTS_ROUTE,
    [PROJECT_ADMIN]: PROJECTS_ROUTE,
    [CONTENT_MANAGER_ADMIN]: PROJECTS_ROUTE,
    [CONTENT_MANAGER]: PROJECTS_ROUTE,
    [EDITORIAL_MANAGER]: PROJECTS_ROUTE,
    [BUSINESS_MANAGER]: PROJECTS_ROUTE,
    [ANALYST_ADMIN]: PROJECTS_ROUTE,
    [ANALYST]: PROJECTS_ROUTE,
    [MODERATOR_ADMIN]: PROJECTS_ROUTE,
    [MODERATOR]: LOGIN_ROUTE,
    [FAST_ADMIN]: FAST_CHANNELS_ROUTE,
    [FAST_ANALYST]: FAST_CHANNELS_ROUTE,
  };

  const getHomePageURL: GetHomePageURL = () => {
    if (!context?.roles[0]) {
      return LOGIN_ROUTE;
    }
    return homePageUrlByRole[context.roles[0]];
  };

  const canAccess = (orgaOptions: OrganizationOptions): CanAccessReturn => {
    const adminUsers = hasRole([OWNER, EDITOR_ADMIN]);
    const castings = hasRole([
      OWNER,
      EDITOR_ADMIN,
      PROJECT_ADMIN,
      CONTENT_MANAGER_ADMIN,
      CONTENT_MANAGER,
      EDITORIAL_MANAGER,
    ]);
    const contents = hasRole([
      OWNER,
      EDITOR_ADMIN,
      PROJECT_ADMIN,
      CONTENT_MANAGER_ADMIN,
      CONTENT_MANAGER,
      EDITORIAL_MANAGER,
      BUSINESS_MANAGER,
      ANALYST_ADMIN,
      ANALYST,
    ]);
    const lives =
      !isFastOnly &&
      orgaOptions?.allow_live_okast &&
      hasRole([
        OWNER,
        EDITOR_ADMIN,
        PROJECT_ADMIN,
        CONTENT_MANAGER_ADMIN,
        CONTENT_MANAGER,
        EDITORIAL_MANAGER,
        BUSINESS_MANAGER,
        ANALYST_ADMIN,
        ANALYST,
      ]);

    return {
      adminUsers,
      bulks:
        orgaOptions?.allow_bulk &&
        hasRole([
          OWNER,
          EDITOR_ADMIN,
          PROJECT_ADMIN,
          CONTENT_MANAGER_ADMIN,
          CONTENT_MANAGER,
        ]),
      insights: adminUsers,
      castings,
      lives,
      contents,
      contentsWrite: hasRole([
        OWNER,
        EDITOR_ADMIN,
        PROJECT_ADMIN,
        CONTENT_MANAGER_ADMIN,
        CONTENT_MANAGER,
        EDITORIAL_MANAGER,
      ]),
      contentsAnalytics: hasRole([
        OWNER,
        EDITOR_ADMIN,
        PROJECT_ADMIN,
        ANALYST,
        ANALYST_ADMIN,
      ]),
      fastChannels:
        !isOttOnly &&
        orgaOptions?.allow_fast &&
        hasRole([OWNER, EDITOR_ADMIN, FAST_ADMIN, FAST_ANALYST]),
      fastChannelsWrite:
        !isOttOnly &&
        orgaOptions?.allow_fast &&
        hasRole([OWNER, EDITOR_ADMIN, FAST_ADMIN]),
      // "Projets" entry, hidden for FAST-only. `contents` is the widest
      // catalogue role check and a superset of the other OTT-side abilities.
      projects: !isFastOnly && contents,
      // "Diffusion FAST" + "Contributions externes": OTT-only catalogue items
      // without a dedicated feature flag.
      ottContents: !isFastOnly,
      storageAccess: hasRole([
        OWNER,
        EDITOR_ADMIN,
        PROJECT_ADMIN,
        CONTENT_MANAGER_ADMIN,
        CONTENT_MANAGER,
      ]),
      storageAccessWrite: hasRole([OWNER, EDITOR_ADMIN]),
      embedProfiles: hasRole([
        OWNER,
        EDITOR_ADMIN,
        PROJECT_ADMIN,
        CONTENT_MANAGER_ADMIN,
        CONTENT_MANAGER,
      ]),
      embedProfilesWrite: hasRole([OWNER, EDITOR_ADMIN]),
      project: {
        allUsers: Boolean(
          getAbility("analytics", "users") && !hasRole([ANALYST]),
        ),
        freeCopies: Boolean(getAbility("offers", "free_copies")),
        gifts: Boolean(getAbility("offers", "gifts")),
        layout: Boolean(getAbility("platform", "layout")),
        configuration: Boolean(getAbility("platform", "configuration")),
        platform: Boolean(getAbility("platform")),
        offers: Boolean(getAbility("offers", "offers")),
        offerManagement: Boolean(getAbility("offers")),
        contentManagement: Boolean(getAbility("contents")),
        analyticManagement: Boolean(getAbility("analytics")),
        dashboard: Boolean(getAbility("dashboard")),
        advertisingsSponsorings: Boolean(
          orgaOptions?.allow_sponso && getAbility("advertising", "sponsoring"),
        ),
        advertisingsSponsoringsAnalytics: Boolean(
          orgaOptions?.allow_sponso && getAbility("analytics", "sponsoring"),
        ),
        displays: Boolean(
          orgaOptions?.allow_display && getAbility("advertising", "display"),
        ),
        displaysAnalytics: Boolean(
          orgaOptions?.allow_display && getAbility("analytics", "display"),
        ),
        notifications: Boolean(
          orgaOptions?.allow_push_notification && getAbility("notifications"),
        ),
        smartlists: Boolean(getAbility("contents", "smartlists")),
        collections: Boolean(getAbility("contents", "collections")),
        series: Boolean(getAbility("contents", "series")),
        smartlistsAudios: Boolean(getAbility("contents", "audio_playlists")),
        bulk: Boolean(
          orgaOptions?.allow_bulk && getAbility("contents", "bulk"),
        ),
        contentsAnalytics: Boolean(getAbility("analytics", "medias")),
        transactionsAnalytics: Boolean(getAbility("analytics", "transactions")),
        offersAnalytics: Boolean(getAbility("analytics", "sales")),
        usersAnalytics: Boolean(getAbility("analytics", "users")),
        community: Boolean(getAbility("community")),
        offersView: Boolean(getAbility("analytics", "transactions")),
        organization: Boolean(getAbility("organization")),
        usersWrite: canWrite("analytics", "users"),
        freeCopiesWrite: canWrite("offers", "free_copies"),
        giftsWrite: canWrite("offers", "gifts"),
        offersWrite: canWrite("offers", "offers"),
        projectWrite:
          canWrite("organization") && canWrite("storefront_project"),
        advertisingsSponsoringsWrite: canWrite("advertising", "sponsoring"),
        displaysWrite: canWrite("advertising", "display"),
        salesWrite: canWrite("analytics", "sales"),
        bulkWrite: canWrite("contents", "bulk"),
        fastWrite: canWrite("fast"),
      },
    };
  };

  return {
    canWrite,
    canWriteOrRead,
    getAbility,
    hasOrgaAbility,
    hasRole,
    canAccess,
    getHomePageURL,
    isFastOnly,
    isOttOnly,
  };
};

export default useAbility;
